import { SceneService } from '../types';
import { NavigateFunction } from 'react-router-dom';
import { v1, toResult, type V1Result } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { sceneToLegacy, type LegacyScene, type SceneStatus } from '@/infrastructure/api/v1/mappers/scenes';
import { storyToLegacy, storyPatchBody, type ChosenVisibility, type LegacyStory } from '@/infrastructure/api/v1/mappers/stories';
import type { operations } from '@/generated/contracts/api-v1';

import type { WriteScope } from '@/modules/undoRedo';
type SceneListQuery = Omit<NonNullable<operations['listScenes']['parameters']['query']>, 'limit' | 'offset'>;

/** A settled call's data passed through `map`, or its problem passed on. */
export function mapResult<T, U>(result: V1Result<T>, map: (data: T) => U): V1Result<U> {
  return result.error ? { data: null, error: result.error } : { data: map(result.data), error: null };
}

/** One scene version as its legacy scene_instances row (getScene). */
export async function fetchSceneRow(sceneId: string): Promise<V1Result<LegacyScene>> {
  return mapResult(await toResult(v1.GET('/api/v1/scenes/{sceneUniqueId}', { params: { path: { sceneUniqueId: sceneId } } })), sceneToLegacy);
}

/** Every scene version matching the filters, as legacy rows in the requested order (listScenes). */
export async function fetchSceneRows(query: SceneListQuery): Promise<V1Result<LegacyScene[]>> {
  const result = await listAll(page => v1.GET('/api/v1/scenes', { params: { query: { ...query, ...page } } }));
  return mapResult(result, scenes => scenes.map(sceneToLegacy));
}

/** One story as its legacy scene_origins row (getStory). */
export async function fetchStoryRow(storyId: string): Promise<V1Result<LegacyStory>> {
  return mapResult(await toResult(v1.GET('/api/v1/stories/{storyUniqueId}', { params: { path: { storyUniqueId: storyId } } })), storyToLegacy);
}

/** Writes legacy scene_origins columns to a story (updateStory). */
export async function updateStoryRow(storyId: string, changes: Partial<LegacyStory>, scope?: WriteScope): Promise<V1Result<LegacyStory>> {
  const result = await toResult(v1.PATCH('/api/v1/stories/{storyUniqueId}', { params: { path: { storyUniqueId: storyId } }, body: storyPatchBody(changes), headers: scope?.headers }));
  return mapResult(result, storyToLegacy);
}

/** Who can find the story (setStoryVisibility); refused with `taken_down` while Lureo has taken it down. */
export async function setStoryVisibility(storyId: string, visibility: ChosenVisibility): Promise<V1Result<null>> {
  return toResult(v1.POST('/api/v1/stories/{storyUniqueId}/visibility', {
    params: { path: { storyUniqueId: storyId } },
    body: { visibility },
  }));
}

/**
 * Moves a scene version through its lifecycle, with every rule the move carries applied by the
 * server: archiving superseded versions, the review submission, the project's published flag
 * (changeSceneStatus). `versionNumber` is taken only with `pending`.
 */
export async function changeSceneStatus(sceneId: string, status: SceneStatus, versionNumber?: number): Promise<V1Result<LegacyScene>> {
  const result = await toResult(v1.POST('/api/v1/scenes/{sceneUniqueId}/status', {
    params: { path: { sceneUniqueId: sceneId } },
    body: status === 'pending' && versionNumber != null ? { status, versionNumber } : { status },
  }));
  return mapResult(result, sceneToLegacy);
}

export class SceneServiceImpl {
  constructor(private readonly sceneService: SceneService) {
    // Bind so the method can be passed as a callback (e.g. from location anchor modal) without losing `this`
    this.updateSceneAnchorCategory = this.updateSceneAnchorCategory.bind(this);
  }

  async deleteSceneInstanceRow(id: string) {
    return this.sceneService.deleteSceneInstanceRow(id);
  }

  async updateSceneCoverImage(sceneInstanceId: string, imageUrl: string) {
    return this.sceneService.updateSceneCoverImage(sceneInstanceId, imageUrl);
  }

  async handleScenePublish(
    sceneStatus: 'live' | 'draft' | 'pending' | 'archived' | 'rejected',
    sceneInstanceId: string,
    versionNumber?: number
  ) {
    return this.sceneService.handleScenePublish(sceneStatus, sceneInstanceId, versionNumber);
  }

  async handleSceneArchive(sceneInstanceId: string) {
    return this.sceneService.handleSceneArchive(sceneInstanceId);
  }

  async countPublishedStories(userId: string) {
    return this.sceneService.countPublishedStories(userId);
  }

  async getShareLinks(sceneOriginId: string) {
    return this.sceneService.getShareLinks(sceneOriginId);
  }

  async mintDraftShareLink(sceneInstanceId: string) {
    return this.sceneService.mintDraftShareLink(sceneInstanceId);
  }

  async mintLiveShareLink(sceneOriginId: string) {
    return this.sceneService.mintLiveShareLink(sceneOriginId);
  }

  async rotateShareLink(slug: string) {
    return this.sceneService.rotateShareLink(slug);
  }

  async revokeShareLink(slug: string) {
    return this.sceneService.revokeShareLink(slug);
  }

  async createNewScene(
    userId: string,
    navigate: NavigateFunction,
    projectInstanceId?: string,
    projectInitials?: string,
    sceneInstancePayload?: Record<string, unknown>,
    options?: { skipNavigate?: boolean }
  ) {
    return this.sceneService.createNewScene(userId, navigate, projectInstanceId, projectInitials, sceneInstancePayload, options);
  }

  async fetchSceneAnchorInstance(sceneInstanceId: string, category: 'immersal' | 'geospatial_anchor' | 'image_anchor') {
    return this.sceneService.fetchSceneAnchorInstance(sceneInstanceId, category);
  }

  async checkSceneAnchors(sceneInstanceId: string) {
    return this.sceneService.checkSceneAnchors(sceneInstanceId);
  }

  async updateSceneAnchorCategory(sceneInstanceId: string, scope?: WriteScope) {
    return this.sceneService.updateSceneAnchorCategory(sceneInstanceId, scope);
  }

  async makeEditableCopy(userId: string, sceneInstanceId: string, projectId: string) {
    return this.sceneService.makeEditableCopy(userId, sceneInstanceId, projectId);
  }

  async unpublishVersion(userId: string, sceneOriginId: string, versionNumber: number, projectId: string) {
    return this.sceneService.unpublishVersion(userId, sceneOriginId, versionNumber, projectId);
  }

  async archiveDraftVersion(userId: string, sceneInstanceId: string) {
    return this.sceneService.archiveDraftVersion(userId, sceneInstanceId);
  }

  async checkProjectHasOtherLiveScenes(projectId: string, currentSceneId?: string) {
    return this.sceneService.checkProjectHasOtherLiveScenes(projectId, currentSceneId);
  }

  async checkUnpublishWillMakeProjectDraft(projectId: string, targetSceneId: string) {
    return this.sceneService.checkUnpublishWillMakeProjectDraft(projectId, targetSceneId);
  }

  async updateSceneInstancePayload(sceneInstanceId: string, payload: Record<string, unknown>, scope?: WriteScope) {
    return this.sceneService.updateSceneInstancePayload(sceneInstanceId, payload, scope);
  }

  async updateSceneOriginName(sceneOriginId: string, newName: string, scope?: WriteScope) {
    return this.sceneService.updateSceneOriginName(sceneOriginId, newName, scope);
  }

  async reorderStories(projectId: string, storyIds: string[]) {
    return this.sceneService.reorderStories(projectId, storyIds);
  }

  async discardDraft(sceneInstanceId: string) {
    return this.sceneService.discardDraft(sceneInstanceId);
  }

  async deleteSceneOriginRow(sceneOriginId: string) {
    return this.sceneService.deleteSceneOriginRow(sceneOriginId);
  }
}
